import React, { useRef, useState } from 'react';
import { Modal, Button, Row, Col, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { FaDownload, FaUpload, FaFileCsv } from 'react-icons/fa';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';
import { downloadBlob } from '../../../common/utils/downloadBlob';
import { buildOrgLearnersCsvTemplate, parseOrgLearnersCsv } from './orgCsvUtils';

const OrgBulkUploadUsersModal = ({ show, onHide, orgId, onImported }) => {
  const fileInputRef = useRef(null);
  const [parsedUsers, setParsedUsers] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [validationSummary, setValidationSummary] = useState(null);
  const [parseError, setParseError] = useState('');
  const [fileName, setFileName] = useState('');
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(null);

  const resetState = () => {
    setParsedUsers([]);
    setValidationResults([]);
    setValidationSummary(null);
    setParseError('');
    setFileName('');
    setImportSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleDownloadTemplate = () => {
    const template = buildOrgLearnersCsvTemplate();
    downloadBlob(template, 'org_learners_bulk_upload_template.csv');
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseError('');
    setValidationResults([]);
    setValidationSummary(null);
    setImportSuccess(null);
    setFileName(file.name);

    try {
      const text = await file.text();
      const records = parseOrgLearnersCsv(text);
      setParsedUsers(records);
      await runValidation(records);
    } catch (error) {
      setParsedUsers([]);
      setParseError(error.message || 'Failed to parse CSV file.');
    }
  };

  const runValidation = async (records) => {
    if (!records.length) {
      setParseError('No user records found in the uploaded file.');
      return;
    }

    try {
      setValidating(true);
      const response = await api.organizations.bulkValidateUsers(orgId, records);
      const payload = response.data || {};
      setValidationResults(Array.isArray(payload.results) ? payload.results : []);
      setValidationSummary(payload.summary || null);
    } catch (error) {
      console.error('Bulk validation error:', error);
      toast.error(error.response?.data?.message || 'Failed to validate uploaded users.');
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!parsedUsers.length || !validationSummary || validationSummary.invalid > 0) {
      return;
    }

    try {
      setImporting(true);
      const response = await api.organizations.bulkImportUsers(orgId, parsedUsers);
      const importedCount = response.data?.importedCount || parsedUsers.length;
      const skipped = response.data?.skipped || 0;
      setImportSuccess({
        count: importedCount,
        skipped,
        message: response.data?.message || `${importedCount} learner(s) imported successfully`
      });
      toast.success(response.data?.message || `${importedCount} learner(s) imported successfully`);
      if (onImported) {
        await onImported();
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      const payload = error.response?.data;
      if (payload?.results) {
        setValidationResults(payload.results);
        setValidationSummary(payload.summary || null);
      }
      toast.error(payload?.message || 'Failed to import users.');
    } finally {
      setImporting(false);
    }
  };

  const allValid = validationSummary && validationSummary.invalid === 0 && parsedUsers.length > 0;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" backdrop="static" fullscreen="sm-down">
      <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
        <Modal.Title className="font-bold text-base sm:text-lg d-flex align-items-center gap-2">
          <FaFileCsv />
          Bulk Upload Organization Learners
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-gray-50 p-3 sm:p-4">
        {importSuccess ? (
          <Alert variant="success" className="mb-0">
            <strong>Upload completed successfully.</strong>
            <div className="mt-2">{importSuccess.message}</div>
            <div className="mt-1">Users created: <strong>{importSuccess.count}</strong></div>
            {importSuccess.skipped > 0 ? (
              <div className="mt-1">Skipped/failed: <strong>{importSuccess.skipped}</strong></div>
            ) : null}
          </Alert>
        ) : (
          <>
            <Alert variant="info" className="text-sm">
              Download the sample CSV template, fill in learner details, and upload it here.
              All users are created as Learners in this organization with Active status.
              Organization group assignment is optional — use semicolon-separated group names from this organization.
            </Alert>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDownloadTemplate}
                className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-sm d-flex align-items-center gap-2 p-2"
              >
                <FaDownload />
                Download Sample CSV
              </Button>

              <Button
                bsPrefix="custom-hamburger"
                onClick={() => fileInputRef.current?.click()}
                className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-sm d-flex align-items-center gap-2 p-2"
              >
                <FaUpload />
                Upload CSV File
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="d-none"
                onChange={handleFileChange}
                aria-label="Upload organization learners CSV file"
              />
            </div>

            {fileName ? (
              <p className="text-sm text-gray-600 mb-3">
                Selected file: <strong>{fileName}</strong> ({parsedUsers.length} record{parsedUsers.length === 1 ? '' : 's'})
              </p>
            ) : null}

            {parseError ? (
              <Alert variant="danger">{parseError}</Alert>
            ) : null}

            {validating ? (
              <div className="d-flex align-items-center gap-2 text-purple-700 py-3">
                <Spinner animation="border" size="sm" />
                Validating uploaded records...
              </div>
            ) : null}

            {validationSummary ? (
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Badge bg="secondary">Total: {validationSummary.total}</Badge>
                  <Badge bg="success">Valid: {validationSummary.valid}</Badge>
                  <Badge bg={validationSummary.invalid > 0 ? 'danger' : 'secondary'}>
                    Errors: {validationSummary.invalid}
                  </Badge>
                  {validationSummary.license_remaining !== undefined ? (
                    <Badge bg="info">Licenses remaining: {validationSummary.license_remaining}</Badge>
                  ) : null}
                </div>

                {validationSummary.invalid > 0 ? (
                  <Alert variant="warning" className="text-sm">
                    Please fix the highlighted errors in your CSV file and upload again. Only valid records can be imported.
                  </Alert>
                ) : (
                  <Alert variant="success" className="text-sm">
                    All records passed validation. You can import users now.
                  </Alert>
                )}
              </div>
            ) : null}

            {validationResults.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-purple-100 bg-white">
                <Table responsive hover size="sm" className="mb-0 text-xs sm:text-sm">
                  <thead className="bg-purple-50">
                    <tr>
                      <th>Row</th>
                      <th>Email</th>
                      <th>Result</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResults.map((result) => {
                      const source = parsedUsers[result.row - 1] || {};
                      return (
                        <tr key={result.row} className={result.valid ? '' : 'table-danger'}>
                          <td>{result.row}</td>
                          <td>{source.email || '-'}</td>
                          <td>
                            <Badge bg={result.valid ? 'success' : 'danger'}>
                              {result.valid ? 'Valid' : 'Invalid'}
                            </Badge>
                          </td>
                          <td className="text-danger">
                            {result.errors?.length ? result.errors.join('; ') : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : null}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-gray-100 border-0 p-3">
        <Row className="w-100 g-2">
          <Col xs={6}>
            <Button
              bsPrefix="custom-hamburger"
              onClick={handleClose}
              disabled={importing}
              className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 font-semibold rounded-lg p-2"
            >
              {importSuccess ? 'Close' : 'Cancel'}
            </Button>
          </Col>
          <Col xs={6}>
            {!importSuccess ? (
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleImport}
                disabled={!allValid || importing || validating}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg p-2"
              >
                {importing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Importing...
                  </>
                ) : (
                  'Upload Users'
                )}
              </Button>
            ) : null}
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default OrgBulkUploadUsersModal;
